import { cn } from "@/libs/cn";
import { GridTableCell, Tag, Tooltip } from "@/modules/common/components";
import {
  MANAGEMENT_COMPANY_STATUS_OPTIONS,
  MANAGEMENT_MODEL_OPTIONS,
  MANAGEMENT_USAGE_TYPE_OPTIONS,
  ManagementCompanyStatus,
  ManagementCompanyUsageType,
  ManagementModel,
} from "@/modules/common/types/ManagementCompanies.enum";
import { AdminManagementCompanyResponse } from "@/service/response/admin-management-companies";

import { gridLayout } from "./ManagerListHeader";
import { formatDate } from "@/modules/common/utils/format";
import { generatePath, Link } from "react-router-dom";
import { ROUTES } from "@/routes/constants/routes";
import { Trash2 } from "lucide-react";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import { AdminUserRole } from "@/modules/common/types/Users.enum";

interface ManagerListLineProps {
  manager: AdminManagementCompanyResponse;
  onInactivate?: (manager: AdminManagementCompanyResponse) => void;
}

const ManagerListLine = ({ manager, onInactivate }: ManagerListLineProps) => {
  const { canUserSee } = useSessionStore();
  const isActiveStatus = manager.status === ManagementCompanyStatus.ACTIVE;
  const selfManaged = manager.managementModel !== null ? manager.managementModel === ManagementModel.SELF_MANAGED : null;
  const statusLabel = MANAGEMENT_COMPANY_STATUS_OPTIONS.find((option) => option.value === String(manager.status))?.label ?? "-";
  const managementModelLabel =
    manager.managementModel !== null
      ? (MANAGEMENT_MODEL_OPTIONS.find((option) => option.value === String(manager.managementModel))?.label ?? "-")
      : "-";
  const isProductionType = manager.usageType === ManagementCompanyUsageType.PRODUCTION;
  const isDemonstrationType = manager.usageType === ManagementCompanyUsageType.DEMONSTRATION;
  const canInactivate = !!onInactivate && isDemonstrationType && canUserSee(AdminUserRole.ADMIN);
  const isAlreadyInactive = manager.status === ManagementCompanyStatus.INACTIVE;
  const usageTypeLabel = MANAGEMENT_USAGE_TYPE_OPTIONS.find((option) => option.value === String(manager.usageType))?.label ?? "-";

  const rowClassName = cn(
    gridLayout,
    "bg-white border border-[#D6DEE9] md:rounded-none md:border-0 md:border-t md:bg-transparent rounded-lg",
    manager.id && "hover:bg-ui-hover transition-colors",
  );

  const cells = (
    <>
      <GridTableCell label="Status">
        <Tag label={statusLabel} variant={isActiveStatus ? "success" : "neutral"} />
      </GridTableCell>

      <GridTableCell label="Nome" className="text-sm text-ui-navy-dark font-medium md:min-w-0">
        <Tooltip label={manager.name}>
          <span className="block truncate">{manager.name}</span>
        </Tooltip>
      </GridTableCell>

      <GridTableCell label="Tipo" className="text-sm text-ui-navy-dark md:min-w-0">
        <Tag label={usageTypeLabel} variant={isProductionType ? "success" : "neutral"} />
      </GridTableCell>

      <GridTableCell label="Modelo de gestão" className="text-sm text-ui-navy-dark md:min-w-0">
        {selfManaged === null ? "-" : <Tag label={managementModelLabel} variant={selfManaged ? "success" : "primary"} />}
      </GridTableCell>

      <GridTableCell label="Representante" className="text-sm text-ui-navy-dark md:min-w-0">
        <Tooltip label={manager.owner?.name} disabled={!manager.owner?.name}>
          <span className="block truncate">{manager.owner?.name ?? "-"}</span>
        </Tooltip>
      </GridTableCell>

      <GridTableCell label="Clientes" className="text-sm text-ui-navy-dark">
        {manager.clientsCount}
      </GridTableCell>

      <GridTableCell label="Usuários" className="text-sm text-ui-navy-dark">
        {manager.usersCount}
      </GridTableCell>

      <GridTableCell label="UCs" className="text-sm text-ui-navy-dark">
        {manager.consumerUnitsCount}
      </GridTableCell>

      <GridTableCell label="Criado em" className="text-sm text-ui-navy-dark">
        {formatDate(manager.createdAt, "DD/MM/YYYY")}
      </GridTableCell>

      <GridTableCell label="Atualizado em" className="text-sm text-ui-navy-dark">
        {formatDate(manager.updatedAt, "DD/MM/YYYY")}
      </GridTableCell>

      <GridTableCell label="Ações" className="md:flex md:justify-end">
        {canInactivate ? (
          <Tooltip label={isAlreadyInactive ? "Gestora já está inativa" : "Inativar gestora"}>
            <button
              type="button"
              // `aria-disabled` em vez de `disabled`: elementos desabilitados não emitem eventos de
              // ponteiro, e o Tooltip do Radix deixaria de aparecer justamente no caso que precisa explicar.
              aria-disabled={isAlreadyInactive}
              aria-label={`Inativar gestora ${manager.name}`}
              className={cn(
                "rounded-md p-1.5 text-[#EC221F]",
                isAlreadyInactive ? "cursor-not-allowed text-[#D1D1D1]" : "cursor-pointer hover:bg-[#FEE9E7]",
              )}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (isAlreadyInactive) return;
                onInactivate?.(manager);
              }}
            >
              <Trash2 size={18} />
            </button>
          </Tooltip>
        ) : null}
      </GridTableCell>
    </>
  );

  if (!manager.id) {
    return <div className={rowClassName}>{cells}</div>;
  }

  return (
    <Link to={generatePath(ROUTES.MANAGER.DETAILS, { id: manager.id })} className={rowClassName}>
      {cells}
    </Link>
  );
};

export default ManagerListLine;
